import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  TextField,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid2 as Grid,
  FormHelperText,
  Card,
  CardContent,
  CardActions,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Public as PublicIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  Money as MoneyIcon,
  Pix as PixIcon,
  LocalHospital as HealthInsuranceIcon,
  AccountBalance as BankTransferIcon,
  Description as BoletoIcon,
  MoreHoriz as OtherIcon,
} from '@mui/icons-material';
import paymentsService from '../services/paymentsService';
import PaymentIconComponent from '../components/ui/PaymentIcon';
import { useContextualHint } from '../hooks/useContextualHint';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PageLayout from '../components/ui/PageLayout';

// Payment types - DEVE corresponder ao enum PaymentType do backend
const PaymentMethodTypes = {
  Cash: { value: 1, label: 'Dinheiro' },
  DebitCard: { value: 2, label: 'Cartão de Débito' },
  CreditCard: { value: 3, label: 'Cartão de Crédito' },
  Pix: { value: 4, label: 'PIX' },
  BankTransfer: { value: 5, label: 'Transferência Bancária' },
  Boleto: { value: 6, label: 'Boleto' },
  HealthInsurance: { value: 7, label: 'Convênio de Saúde' },
  Other: { value: 99, label: 'Outro' },
};

const getPaymentTypeLabel = (typeValue) => {
  const type = Object.values(PaymentMethodTypes).find(t => t.value === typeValue);
  return type ? type.label : 'Desconhecido';
};

// Mapeia type para ícone do Material-UI
const getPaymentIcon = (typeValue) => {
  switch(typeValue) {
    case 1: return <MoneyIcon />;           // Cash
    case 2: return <CreditCardIcon />;      // DebitCard
    case 3: return <CreditCardIcon />;      // CreditCard
    case 4: return <PixIcon />;             // Pix
    case 5: return <BankTransferIcon />;    // BankTransfer
    case 6: return <BoletoIcon />;          // Boleto
    case 7: return <HealthInsuranceIcon />; // HealthInsurance
    case 99: return <OtherIcon />;          // Other
    default: return <PaymentIcon />;
  }
};

export default function PaymentMethodsPage() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
  const [formData, setFormData] = useState({
    type: 1, // Default: Cash
    name: '',
    description: '',
    icon: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const hint = useContextualHint('payments', {});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const payments = await paymentsService.getAll();
      // Ordenar: customizados primeiro, depois globais
      const sorted = (payments || []).sort((a, b) => {
        if (a.isGlobal === b.isGlobal) return 0;
        return a.isGlobal ? 1 : -1; // customizados (false) antes de globais (true)
      });
      setPaymentMethods(sorted);
    } catch (error) {
      showSnackbar('Erro ao carregar dados: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (paymentMethod = null) => {
    if (paymentMethod) {
      // Apenas payment methods customizados podem ser editados
      if (paymentMethod.isGlobal) {
        showSnackbar('Formas de pagamento globais não podem ser editadas', 'warning');
        return;
      }
      setCurrentPaymentMethod(paymentMethod);
      setFormData({
        type: paymentMethod.type,
        name: paymentMethod.name,
        description: paymentMethod.description || '',
        icon: paymentMethod.icon || '',
      });
    } else {
      setCurrentPaymentMethod(null);
      setFormData({
        type: 1, // Default: Cash
        name: '',
        description: '',
        icon: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPaymentMethod(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'type' ? parseInt(value) : value,
    });
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        type: parseInt(formData.type),
        name: formData.name,
        description: formData.description || null,
        icon: formData.icon || null,
      };

      if (currentPaymentMethod) {
        await paymentsService.update(currentPaymentMethod.id, payload);
        showSnackbar('Método de pagamento atualizado com sucesso!');
      } else {
        await paymentsService.create(payload);
        showSnackbar('Método de pagamento criado com sucesso!');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      showSnackbar('Erro ao salvar: ' + (error.response?.data?.message || error.message), 'error');
    }
  };

  const handleOpenDeleteDialog = (paymentMethod) => {
    // Não permitir deletar payment methods globais
    if (paymentMethod.isGlobal) {
      showSnackbar('Formas de pagamento globais não podem ser excluídas', 'warning');
      return;
    }
    setCurrentPaymentMethod(paymentMethod);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentPaymentMethod(null);
  };

  const getTypeChipColor = (type) => {
    switch (type) {
      case 4: // HealthInsurance
      case 5: // Insurance
        return 'primary';
      case 0: // Cash
        return 'success';
      case 1: // CreditCard
      case 2: // DebitCard
      case 3: // Pix
        return 'secondary';
      default:
        return 'default';
    }
  };

  const isInsuranceType = (type) => type === 4 || type === 5; // HealthInsurance ou Insurance

  return (
    <PageLayout
      themeKey="paymentMethods"
      subtitle="Cadastre e gerencie formas de pagamento para seus serviços"
      actionLabel="Novo Método Customizado"
      onAction={() => handleOpenDialog()}
      hint={hint}
      infoBanner="Cadastre formas de pagamento para vincular com lugares e profissionais. Já temos algumas pré-cadastradas, mas você pode criar quantas quiser para usar depois."
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : paymentMethods.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '2px dashed', borderColor: 'divider' }}>
          <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Nenhum método de pagamento encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Crie métodos customizados para seus serviços
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Criar Primeiro Método
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {paymentMethods.map((pm) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={pm.id}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main'
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Header compacto */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    {pm.isGlobal ? (
                      // Para pagamentos globais, usar sprite de ícones
                      <PaymentIconComponent 
                        displayOrder={pm.displayOrder || 1}
                        size={40}
                      />
                    ) : (
                      // Para pagamentos customizados, usar ícone MUI
                      <Box 
                        sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontSize: '1.25rem'
                        }}
                      >
                        {getPaymentIcon(pm.type)}
                      </Box>
                    )}
                    <Box flexGrow={1} sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" component="div" noWrap sx={{ fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3 }}>
                        {pm.name}
                      </Typography>
                      {pm.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
                          {pm.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Badges compactos */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {pm.isGlobal ? (
                      <Chip
                        icon={<PublicIcon sx={{ fontSize: '0.875rem' }} />}
                        label="Somente Leitura"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                      />
                    ) : (
                      <Chip
                        icon={<PersonIcon sx={{ fontSize: '0.875rem' }} />}
                        label="Customizado"
                        size="small"
                        color="secondary"
                        sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                      />
                    )}
                  </Box>
                </CardContent>

                {/* Ações compactas */}
                {!pm.isGlobal && (
                  <CardActions sx={{ justifyContent: 'flex-end', p: 1, pt: 0 }}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(pm)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(pm)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de Criar/Editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentPaymentMethod ? 'Editar Método de Pagamento' : 'Novo Método de Pagamento'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Tipo"
                  >
                    {Object.entries(PaymentMethodTypes).map(([key, type]) => (
                      <MenuItem key={key} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Ícone"
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Ex: 💳, 💰, 🏥"
                  helperText="Emoji ou ícone para representar este método"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  fullWidth
                  placeholder="Ex: Unimed Curitiba, Plano XYZ"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Descrição"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Informações adicionais sobre este método de pagamento"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Este método ficará disponível apenas para você vincular aos seus serviços.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <CascadeDeleteDialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onDeleted={() => {
          handleCloseDeleteDialog();
          showSnackbar('Excluído com sucesso!');
          loadData();
        }}
        deletePath={`/payments/${currentPaymentMethod?.id}`}
        entityName={currentPaymentMethod?.name}
      />

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}